import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import {engravedPlateExtendedStore} from './engraved-plate-extended.store';
import {EPMethod, PCS_CSS, ProductCustomizationState} from '../../../rendering/render.def';
import {isNullOrWhiteSpace} from '../../../../util';

type Props = {
    css: PCS_CSS;
    pcs: ProductCustomizationState;
};

export const EPLines: React.FC<Props> = observer(({pcs, css}: Props) => {
    const baseLines = [1, 2, 3];
    const extendedLines = pcs.EPMethodID === EPMethod.Extended ? [4, 5, 6, 7, 8, 9, 10] : [];
    const linesToRender = [...baseLines, ...extendedLines];
    const plateHeightPt = css.ep_plateHeightPt || 72;
    const containerHeightPx = parseFloat(css.ep_height) || pcs.EPHeight * pcs.DPI;

    const getLineStyle = useCallback(
        (idx: number) => {
            const value = pcs[`EP_${idx}`];
            const line = engravedPlateExtendedStore.lines[idx - 1];

            const baseStyle: React.CSSProperties = {
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
            };

            if (!value || isNullOrWhiteSpace(value)) {
                return {
                    ...baseStyle,
                    fontWeight: 400,
                    fontStyle: 'normal' as const,
                    fontFamily: engravedPlateExtendedStore.fontFamily,
                };
            }

            const fontSizePx = line?.size ? `${((line.size / plateHeightPt) * containerHeightPx).toFixed(4)}px` : undefined;

            return {
                ...baseStyle,
                fontSize: fontSizePx,
                lineHeight: 1,
                fontWeight: line?.bold ? 700 : 400,
                fontStyle: line?.italic ? 'italic' : ('normal' as const),
                fontFamily: engravedPlateExtendedStore.fontFamily,
            };
        },
        [pcs, plateHeightPt, containerHeightPx],
    );

    const epLinesStyle: React.CSSProperties = {
        fontSize: css.ep_lines_fontSize,
        width: '100%',
        height: '100%',
        position: 'relative',
        boxSizing: 'border-box',
        padding: 0,
    };

    return (
        <div
            id="ep_lines"
            style={epLinesStyle}
        >
            {linesToRender.map(idx => {
                const value = pcs[`EP_${idx}`];

                if (!value) return null;

                if (pcs.EPMethodID === EPMethod.Extended) {
                    return (
                        <div
                            key={idx}
                            id={`ep_line${idx}`}
                            style={getLineStyle(idx)}
                        >
                            {value}
                        </div>
                    );
                }

                return (
                    <div
                        key={idx}
                        id={`ep_line${idx}`}
                        style={pcs.EPMethodID === EPMethod.Old ? {fontSize: css.ep_lines_fontSize, lineHeight: css.ep_lines_lineHeight} : undefined}
                    >
                        {value}
                    </div>
                );
            })}
        </div>
    );
});
